import React, { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  Edit,
  Trash2,
  Star,
  Mail,
  User,
  Search,
  MoreHorizontal,
  Archive,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { adminApi, type AgentAvatarRef } from '@/lib/api'
import { adminAgentAvatarHost } from '@/lib/agentAvatarHost'
import { AgentAvatar, AgentAvatarProvider } from '@ponder/artifacts/agent-avatar'
import { AvatarPicker, applyAvatarChoice, type AvatarChoice } from '@/components/AvatarPicker'
import { AgentEditDialog } from '@/components/AgentEditDialog'

// Types based on backend agent model
interface Agent {
  id: string
  email: string
  name: string
  description?: string
  shortDescription?: string
  /** The resolved Avatar artifact ref (binding, template, else the org default). */
  avatar?: AgentAvatarRef | null
  correspondenceCount?: number
  stars?: number
  messagesSentCount?: number
  isActive?: boolean
  visibility?: string
  isDefault: boolean
  ownerId?: string | null
  isArchived?: boolean
  createdAt?: string
  updatedAt?: string
}


interface CreateAgentData {
  id?: string
  email: string
  name: string
  type?: string
  description?: string
  shortDescription?: string
  isDefault?: boolean
}

/** The agent's resolved avatar, drawn through the one agent avatar seam. */
function AgentRowAvatar({ agent }: { agent: Agent }) {
  return (
    <AgentAvatarProvider host={adminAgentAvatarHost}>
      <AgentAvatar agent={agent} size="md" decorative />
    </AgentAvatarProvider>
  )
}

const PAGE_SIZE = 50
const VISIBILITY_OPTIONS = [
  { value: 'all', label: 'All Visibilities' },
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'beta', label: 'Beta' },
  { value: 'system', label: 'System' },
]

export function AgentsManagementPage() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [createError, setCreateError] = useState('')
  // The new agent's avatar: undefined = the org default (D4), else an Avatar artifact to bind
  const [createAvatarChoice, setCreateAvatarChoice] = useState<AvatarChoice>(undefined)
  const [formData, setFormData] = useState<CreateAgentData>({
    id: '',
    email: '',
    name: '',
    type: 'Correspondent',
    description: '',
    shortDescription: '',
    isDefault: false
  })

  // Load agents from API with pagination and visibility filter
  const loadAgents = useCallback(async () => {
    try {
      setLoading(true)
      const response = await adminApi.getAgents({
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        visibility: visibilityFilter === 'all' ? undefined : visibilityFilter,
      })

      const agentsArray = Array.isArray(response.data) ? response.data : (response.data?.agents || [])
      setAgents(agentsArray)
      setTotalCount(response.totalCount || agentsArray.length)
    } catch (error) {
      console.error('Failed to load agents:', error)
    } finally {
      setLoading(false)
    }
  }, [page, visibilityFilter])

  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  // Reset to first page when filter changes
  const handleVisibilityChange = (value: string) => {
    setVisibilityFilter(value)
    setPage(0)
  }

  // Filter agents based on search term
  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Handle form submission for create
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError('')
    try {
      // Create new agent, then bind the chosen avatar (never avatar_url)
      const created = await adminApi.createAgent(formData)
      if (created?.id && typeof createAvatarChoice === 'string') {
        try {
          await applyAvatarChoice(created.id, createAvatarChoice)
        } catch (avatarError) {
          console.error('Agent created, but its avatar could not be set:', avatarError)
        }
      }
      setCreateAvatarChoice(undefined)
      setIsCreateDialogOpen(false)
      
      // Reload agents
      await loadAgents()
      
      // Reset form
      setFormData({
        id: '',
        email: '',
        name: '',
        type: 'Correspondent',
        description: '',
        shortDescription: '',
        isDefault: false
      })
    } catch (error: any) {
      console.error('Failed to create agent:', error)
      // Extract error message from response
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to create agent'
      setCreateError(errorMessage)
    }
  }

  // Handle delete agent
  const handleDelete = async () => {
    if (!selectedAgent) return
    
    try {
      await adminApi.deleteAgent(selectedAgent.id)
      setIsDeleteDialogOpen(false)
      setSelectedAgent(null)
      await loadAgents()
    } catch (error) {
      console.error('Failed to delete agent:', error)
    }
  }

  // Open edit dialog with selected agent
  const openEditDialog = (agent: Agent) => {
    setSelectedAgent(agent)
    setIsEditDialogOpen(true)
  }

  // Open delete dialog
  const openDeleteDialog = (agent: Agent) => {
    setSelectedAgent(agent)
    setIsDeleteDialogOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Agents Management</h1>
          <p className="text-muted-foreground">
            Manage AI agents, their configurations, and performance metrics.
          </p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
          setIsCreateDialogOpen(open)
          if (!open) setCreateError('') // Clear error when dialog closes
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Agent
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Agent</DialogTitle>
              <DialogDescription>
                Create a new AI agent with custom configuration.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="id">
                    Agent ID (Optional)
                    <span className="text-xs text-muted-foreground ml-2">
                      Leave blank to auto-generate
                    </span>
                  </Label>
                  <Input
                    id="id"
                    value={formData.id}
                    onChange={(e) => setFormData({...formData, id: e.target.value})}
                    placeholder="00000000-0000-0000-0000-000000000000"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">
                    Email Handle
                    <span className="text-xs text-muted-foreground ml-2">
                      (without @domain)
                    </span>
                  </Label>
                  <Input
                    id="email"
                    type="text"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="ponder"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Agent will be accessible at {formData.email || 'handle'}@{window.location.hostname.replace('admin.', '')}
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Agent Name"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="shortDescription">Short Description</Label>
                  <Input
                    id="shortDescription"
                    value={formData.shortDescription}
                    onChange={(e) => setFormData({...formData, shortDescription: e.target.value})}
                    placeholder="Brief description..."
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed description of the agent's capabilities..."
                    rows={3}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Avatar</Label>
                  <AvatarPicker choice={createAvatarChoice} onChoose={setCreateAvatarChoice} />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({...formData, isDefault: e.target.checked})}
                  />
                  <Label htmlFor="isDefault">Set as default agent</Label>
                </div>
              </div>
              {createError && (
                <div className="flex items-center space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <div className="text-destructive text-sm">{createError}</div>
                </div>
              )}
              <DialogFooter>
                <Button type="submit">Create Agent</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search agents by name, email, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={visibilityFilter} onValueChange={handleVisibilityChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Visibility" />
          </SelectTrigger>
          <SelectContent>
            {VISIBILITY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Agents</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agents.filter(a => a.isActive !== false).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agents.reduce((sum, a) => sum + (a.messagesSentCount || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Stars</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agents.reduce((sum, a) => sum + (a.stars || 0), 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Agents</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Stars</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAgents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <AgentRowAvatar agent={agent} />
                        <div>
                          <div className="font-medium">{agent.name}</div>
                          {agent.shortDescription && (
                            <div className="text-sm text-muted-foreground">
                              {agent.shortDescription}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{agent.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={agent.visibility === 'public' ? 'default' : 'outline'}
                        className={`capitalize ${agent.visibility === 'public' ? 'bg-green-100 text-green-800' : ''}`}
                      >
                        {agent.visibility || 'public'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {agent.isActive !== false ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                        {agent.isDefault && (
                          <Badge variant="outline">Default</Badge>
                        )}
                        {agent.isArchived && (
                          <Badge variant="destructive">Archived</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{agent.messagesSentCount || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400" />
                        <span>{agent.stars || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => openEditDialog(agent)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(agent)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page + 1} of {Math.ceil(totalCount / PAGE_SIZE)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= totalCount}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Edit Dialog */}
      <AgentEditDialog
        agent={selectedAgent}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSave={loadAgents}
      />

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedAgent?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AgentsManagementPage